import { useGetNewsMutation, useMarkAllNewsAsSeenMutation, useMarkNewsAsSeenMutation } from '@services/news/news.endpoints';
import { News } from '@services/news/news.types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOnClickOutside } from 'usehooks-ts';

export const useNewsPageVM = () => {
    const [news, setNews] = useState<Array<News>>([]);
    const [selectedNews, setSelectedNews] = useState<News | null>(null);
    const [getNews, { isLoading, data }] = useGetNewsMutation();
    const [markNewsAsSeen] = useMarkNewsAsSeenMutation();
    const [markAllNewsAsSeen, { isLoading: isMarkingAllAsSeen }] = useMarkAllNewsAsSeenMutation();

    const [searchParams] = useSearchParams();
    const id = searchParams.get('id');

    const selectedNewsRef = useRef<HTMLDivElement>(null);

    const markLocallyAsSeen = useCallback((id: string) => {
        setNews((current) => current.map((n) => (n.id === id ? { ...n, seen: true } : n)));
        setSelectedNews((current) => (current && current.id === id ? { ...current, seen: true } : current));
    }, []);

    const handleSelectNews = useCallback(
        (next: News | null) => {
            setSelectedNews(next);

            if (next && !next.seen) {
                markLocallyAsSeen(next.id);
                markNewsAsSeen(next.id);
            }
        },
        [markLocallyAsSeen, markNewsAsSeen]
    );

    const handleMarkAllAsRead = useCallback(() => {
        if (!news.some((n) => !n.seen)) {
            return;
        }
        setNews((current) => current.map((n) => ({ ...n, seen: true })));
        markAllNewsAsSeen();
    }, [news, markAllNewsAsSeen]);

    useOnClickOutside(selectedNewsRef, () => {
        setSelectedNews(null);
    });

    useEffect(() => {
        if (selectedNews) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [selectedNews]);

    useEffect(() => {
        getNews();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isLoading) {
            return;
        }
        if (data) {
            setNews(data);
            if (id) {
                const target = data.find((n) => n.id === id);
                if (target) {
                    setSelectedNews(target);
                    if (!target.seen) {
                        markLocallyAsSeen(target.id);
                        markNewsAsSeen(target.id);
                    }
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, isLoading, id]);

    const unreadNews = useMemo(() => news.filter((n) => !n.seen), [news]);

    return {
        selectedNewsRef,
        news,
        unreadNews,
        unreadCount: unreadNews.length,
        isLoading,
        isMarkingAllAsSeen,
        selectedNews,
        handleSelectNews,
        handleMarkAllAsRead
    };
};
